import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaArrowLeft, FaPlus, FaSave, FaStream, FaUndo } from 'react-icons/fa';
import { AccordionItem, AccordionPanel, Box, Flex, Heading, HStack, VStack } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { UniqueIdentifier } from '@dnd-kit/core';
import AssetManagerPage from 'components/common/AssetManagerPage';
import Layout from 'components/common/Layout';
import { useFetchProduct, useToast, useUpdateProduct } from 'hooks';
import {
  Icon,
  IconButton,
  Link,
  Modal,
  ModalBody,
  Text,
  Accordion,
  AccordionButton,
  CardTitle,
  Spinner,
} from 'components/UI';
import { LinkButton } from 'components/common/Buttons/LinkButton';
import ParameterEdit from 'components/productManager/ParameterEdit';
import AddParameter from 'components/productManager/AddParameter';
import { ParameterValueProps } from 'components/productManager/ParameterValues/ParameterValue';
import { removeObjectItem } from 'utils/helpers';
import { Pages } from 'constants/pages';
import AppRoutes from 'constants/routes';
import { bodyFont, primaryThemeGradientDark, specialFont } from 'constants/theme';
import { parameterModels } from 'constants/parameterModels';
import CenterLoader from 'components/common/CenterLoader';
import SortableContainerList, { SortableContainerSubItems } from 'components/productManager/SortableContainerList';
import { IConditions, IParameter, IParameterList, IParameterModels, IProductResource } from '../../../server/types';

const EditParameters = () => {
  const params = useParams();

  const productKey = params.productId ? params.productId : undefined;

  const [fetchProduct] = useFetchProduct();
  const [updateProduct] = useUpdateProduct();

  const [showToast] = useToast();

  const [parameterList, setParameterList] = useState<IParameterList[] | (string | boolean | string[])[][]>([]);

  const [primaryContainer, setPrimaryContainer] = useState<UniqueIdentifier>();
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [product, setProduct] = useState<IProductResource>();
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [addParameter, setAddParameter] = useState(false);
  const [sortParameters, setSortParameters] = useState(false);
  const [reportUrl, setReportUrl] = useState('');
  const [message, setMessage] = useState('');

  const handleFilterConditions = (param: IParameter) =>
    Object.entries(param).reduce((acc, [key, value]) => {
      const isRemoveOnConditions =
        key === 'onConditions' && param.onConditions && param.onConditions.expressionList.length === 0;

      const isRemoveValueConditions =
        key === 'valueConditions' &&
        param.valueConditions &&
        (param.valueConditions as IConditions).expressionList.length === 0;

      if (isRemoveOnConditions || isRemoveValueConditions) {
        return acc;
      }

      return { ...acc, [key]: value };
    }, {});

  const handleFilterExpressions = (filterParams: IParameter[]) =>
    filterParams.reduce((acc, param) => {
      const filteredParam = handleFilterConditions(param);

      return [...acc, filteredParam as IParameter];
    }, [] as IParameter[]);

  const handleUpdateParameters = (key: string | number, name: string | number, value: string | boolean) => {
    const transformedValue =
      name === 'values' && typeof value === 'object' && !Array.isArray(value)
        ? (Object.values(value) as string[])
        : value;
    const defaultBooleanValuesData =
      name === 'type' && value === 'boolean'
        ? {
            values: [
              { displayName: '', value: true },
              { displayName: '', value: false },
            ],
          }
        : {};

    setParameters((prevParameters) => ({
      ...prevParameters,
      [key]: { ...prevParameters[key as number], [name]: transformedValue, ...defaultBooleanValuesData },
    }));
  };

  const handleFindEmptyCategory = () => {
    const emptyCategory = (parameterList as (string | boolean | string[])[][]).find(
      (param) => Array.isArray(param[1]) && param[1].length === 0
    );

    return emptyCategory;
  };

  const handleSaveParameters = async () => {
    const emptyCategory = handleFindEmptyCategory();
    if (emptyCategory) {
      showToast({
        message: `Save parameters failed; Please add parameters to ${emptyCategory[0]}.`,
        status: 'error',
      });
      return;
    }

    if (product?.parameterList === undefined) {
      setParameterList(parameterList);
    }

    const invalidValueConditions = Object.values(parameters).filter((param) => {
      const { valueConditions } = param as IParameter;
      return (
        valueConditions &&
        Object.values((valueConditions as IConditions).expressions).some(({ expression }) => !expression)
      );
    });
    if (invalidValueConditions.length > 0) {
      showToast({
        message: `Save parameters failed; Please remove empty value condition from parameters ${invalidValueConditions
          .map((param) => param.displayName)
          .join(', ')}`,
        status: 'error',
      });
      return;
    }

    const newParams = handleFilterExpressions(Object.values(parameters) as IParameter[]);

    const transformedParams = newParams.map((param) => {
      if (param.values && typeof param.values === 'object' && !Array.isArray(param.values)) {
        return { ...param, values: Object.values(param.values) };
      }
      return param;
    });
    const newParamList = parameterList.map((arr) => {
      const [title, pList, primary] = arr as (string | boolean | string[])[];
      const primaryObj = primary ? { primary: true } : {};

      return { title, pList, ...primaryObj };
    });

    const body = {
      displayName,
      parameterList: newParamList,
      parameters: transformedParams,
    };

    await updateProduct(product?.id!, body as IProductResource);
    showToast({
      message: 'Parameters saved successfully.',
    });
  };

  const deleteParameter = (deleteKey: string | number | undefined) => {
    if (!deleteKey) return;

    setParameterList((prevParamList) => {
      const updatedParamList = prevParamList.map((param) => {
        const [title, pList, primary] = param as (string | boolean | string[])[];
        const updatedValues = (pList as string[])
          .filter((value) => value !== deleteKey)
          .map((value) => (+value > +deleteKey ? (+value - 1).toString() : value));

        const primaryArr = primary ? [primary] : [];

        return [title, updatedValues, ...primaryArr];
      });
      return updatedParamList;
    });

    setParameters((prevParameters) => {
      const newParameters = Object.keys(prevParameters)
        .filter((key) => key !== deleteKey)
        .reduce((acc, key, index) => {
          const sequencedParameter = {
            ...prevParameters[key],
            [key]: index,
          };

          const updatedObj = Object.entries(sequencedParameter).reduce((acc2, [key2, value]) => {
            if (key2 === deleteKey) return acc;

            return { ...acc2, [key2]: value };
          }, {});

          return { ...acc, [index]: updatedObj };
        }, {});

      return newParameters;
    });
  };

  const handleDeleteExpression: ParameterValueProps['deleteExpression'] = (paramKey, key, deleteKey) =>
    setParameters((prevParameters) => ({
      ...prevParameters,
      [paramKey]: {
        ...prevParameters[paramKey as number],
        [key]: {
          ...parameters[paramKey][key],
          expressions: removeObjectItem(prevParameters[paramKey][key].expressions, deleteKey as string),
          expressionList: (prevParameters[paramKey][key].expressionList as string[]).filter(
            (expression) => expression !== deleteKey
          ),
        },
      },
    }));

  const handleDeleteListItem: ParameterValueProps['deleteListItem'] = (paramKey, key, deleteKey) =>
    setParameters((prevParameters) => ({
      ...prevParameters,
      [paramKey]: {
        ...prevParameters[paramKey as number],
        [key]: removeObjectItem(parameters[paramKey][key], deleteKey.toString()),
      },
    }));

  const handleLoadParameters = async () => {
    setLoading(true);

    const currentProduct = productKey ? ((await fetchProduct(productKey)) as IProductResource) : undefined;

    if (!currentProduct?.parameters) return;

    const parameterListContainerPrimary = currentProduct?.parameterList
      ? currentProduct?.parameterList.find(({ primary }: IParameterList) => primary)
      : null;

    const newParamList = currentProduct.parameterList
      .map(({ title, pList, primary }: IParameterList) => {
        const primaryArr = primary ? ['primary'] : [];

        return [title, pList, ...primaryArr];
      })
      .concat();

    setPrimaryContainer(parameterListContainerPrimary?.title);
    setProduct(currentProduct);
    setDisplayName(currentProduct.displayName!);
    setLoading(false);
    setParameters(currentProduct.parameters);
    setParameterList(newParamList ?? []);
  };

  const handleGetModel = (type: string, copyCurrentModel = {}) => {
    switch (type) {
      case 'driving':
        return parameterModels?.drivingParameterModel;
      case 'note':
        return parameterModels?.noteModel;
      case 'copy':
        return copyCurrentModel;
      case 'reference':
        return parameterModels?.referenceParameterModel;
      default:
        return undefined;
    }
  };

  const handleAddParameter = (key: string | number, type: string, copyCurrentModel = {}) => {
    const model = handleGetModel(type, copyCurrentModel);

    const newParam = Object.entries(model as IParameterModels).reduce((acc, [paramKey, paramValue]) => {
      if (paramValue.default === undefined) return acc;

      return { ...acc, [paramKey]: paramValue.default };
    }, {});

    setParameterList((prevParameterList) => {
      const index = prevParameterList.findIndex((arr) => (arr as (string | boolean | string[])[]).includes('primary'));
      const newParameterList = [...prevParameterList] as (string | boolean | string[])[][];
      newParameterList[index][1] = [...(newParameterList[index][1] as string[]), key as string];

      return newParameterList;
    });

    setParameters((prevState) => ({ ...prevState, [key]: newParam }));

    setAddParameter(false);
    showToast({
      message: 'Parameter added successfully.',
    });
  };

  const handleResetParameters = async () => {
    await handleLoadParameters();
    showToast({
      message: 'Parameters reset successfully.',
    });
  };

  const handleUpdateContainerItems = (orderedList: SortableContainerSubItems[]) => {
    setParameterList(orderedList as string[][]);
  };

  const renderLoading = loading ? (
    <CenterLoader>
      <Spinner size="xl" thickness="5px" />
    </CenterLoader>
  ) : null;

  const renderAddParameter = addParameter ? (
    <AddParameter
      addParameter={handleAddParameter}
      parameterList={parameterList as [string | boolean | string[]][]}
      onCancel={() => setAddParameter(false)}
    />
  ) : null;

  const renderSortableList = Object.entries(parameters).reduce(
    (acc, [key, val]) => ({
      ...acc,
      [key]: (
        <CardTitle fontFamily={bodyFont} letterSpacing="normal">
          {val.displayName}
        </CardTitle>
      ),
    }),
    {}
  );

  const renderParameterList = (parameterList as (string | string | string[])[][]).map(
    (param: (string | string[] | string)[]) => (
      <AccordionItem borderTopWidth={0} mb={0.5}>
        <AccordionButton
          sx={{
            bg: 'primaryThemeColour.400',
            _hover: {
              bg: 'primaryThemeColour.500',
            },
            color: 'primaryFontColour.400',
            fontFamily: specialFont,
            letterSpacing: 3,
          }}
        >
          {param[0]}
        </AccordionButton>
        <AccordionPanel p={0}>
          <Accordion allowMultiple>
            {(param[1] as string[]).map((key: string | number) => (
              <ParameterEdit
                parameter={parameters[+key]}
                parameterModels={parameterModels}
                updateSaveParameters={handleUpdateParameters}
                paramKey={key}
                key={key}
                deleteListItem={handleDeleteListItem}
                deleteExpression={handleDeleteExpression}
                deleteParameter={deleteParameter}
                addParameter={handleAddParameter}
                parameterList={parameterList}
              />
            ))}
          </Accordion>
        </AccordionPanel>
      </AccordionItem>
    )
  );

  const renderReportLink = reportUrl ? <Link href={reportUrl}>Download the report.</Link> : null;

  useEffect(() => {
    handleLoadParameters();
  }, []);

  const toggleColour = sortParameters ? 'black' : primaryThemeGradientDark;

  const renderParameters = sortParameters ? (
    <Flex flex={1} flexDir="column">
      <Heading
        as="h2"
        sx={{
          letterSpacing: 'wide',
          fontSize: '3xl',
          fontWeight: 'medium',
          color: 'gray.600',
          mb: 8,
        }}
      >
        DRAG TO RE-ORDER
      </Heading>
      <Box>
        <SortableContainerList
          containers={parameterList as SortableContainerSubItems[]}
          items={renderSortableList}
          primary={primaryContainer}
          onUpdatePrimary={(newTitle: UniqueIdentifier) => setPrimaryContainer(newTitle)}
          onUpdateContainerItems={handleUpdateContainerItems}
        />
      </Box>
    </Flex>
  ) : (
    parameterList.length > 0 && (
      <Accordion allowMultiple defaultIndex={[...Array(parameterList.length).keys()]}>
        {renderParameterList}
      </Accordion>
    )
  );
  return (
    <Layout pages={[Pages.ProductManager]}>
      <Helmet title={`${product?.displayName ?? 'Edit '} Parameters`} />
      <AssetManagerPage title="Edit Product Parameters">
        {renderAddParameter}
        {renderLoading}
        <HStack px={40}>
          <VStack
            sx={{
              position: 'fixed',
              gap: 2,
              top: 245,
              right: 95,
            }}
          >
            <IconButton
              aria-label="Reset Parameters"
              title="Reset Parameters"
              h={12}
              w={12}
              fontSize="2xl"
              icon={<FaUndo />}
              onClick={handleResetParameters}
            />
            <IconButton
              aria-label="Add Parameter"
              title="Add Parameter"
              h={12}
              w={12}
              fontSize="2xl"
              icon={<FaPlus />}
              onClick={() => setAddParameter(true)}
            />
            <IconButton
              aria-label="Toggle Reorder Parameters"
              title="Toggle Reorder Parameters"
              h={12}
              w={12}
              fontSize="2xl"
              bg={toggleColour}
              _hover={{
                bg: toggleColour,
              }}
              icon={<FaStream />}
              onClick={() => setSortParameters((prevSortParameters) => !prevSortParameters)}
            />
            <IconButton
              aria-label="Save Parameters"
              title="Save Parameters"
              h={12}
              w={12}
              fontSize="2xl"
              icon={<FaSave />}
              onClick={handleSaveParameters}
            />
            <LinkButton
              to={AppRoutes.ProductManager}
              sx={{
                height: 12,
                width: 12,
              }}
            >
              <Icon as={FaArrowLeft} />
            </LinkButton>
          </VStack>

          <Modal
            isOpen={!!message}
            isCloseButton
            closeOnEsc={false}
            closeOnOverlayClick
            isCentered
            size="xs"
            onClose={() => {
              setMessage('');
              setReportUrl('');
            }}
          >
            <ModalBody>
              <Text>{message}</Text>
              <Text>{renderReportLink}</Text>
            </ModalBody>
          </Modal>

          <Flex flex={1} flexDir="column" gap={4}>
            {product !== null ? renderParameters : null}
          </Flex>
        </HStack>
      </AssetManagerPage>
    </Layout>
  );
};

export default EditParameters;
